import { useMemo } from 'react';
import { omitBy, isNil } from 'lodash';

import { ensureDefined } from '@e-socna/utils';
import { CodingStrategyEnum, GroupingModeEnum } from '@e-socna/data-layer';
import { EMPTY_ARRAY, EMPTY_STRING } from '@e-socna/empty';
import { EMPTY_ID } from '@e-socna/empty-client';

import { useIdFactory } from '../../../hooks/useIdFactory.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useDeepMemo } from '../../../hooks/useDeepMemo.hook';

import { useInitialValuesFactory as useQuotationHeaderInitialValuesFactory } from '../../QuotationHeader/hooks/useInitialValuesFactory.hook';

import type { ResolvableMissionTypeForm_ResolvableMissionType } from '../fragments/resolvable-mission-type.fragment';

export const useInitialValuesFactory = (
  pInitialValues?: Readonly<ResolvableMissionTypeForm_ResolvableMissionType>,
) => {
  const { generateId } = useIdFactory();

  const referencesContext = useReferencesContext();

  const { defaultGenericStatus } = ensureDefined({
    defaultGenericStatus: referencesContext.defaultGenericStatus,
  });

  const { codingStrategiesCodeMap } = referencesContext;

  const quotationHeaderInput = useDeepMemo(
    () => omitBy(pInitialValues?.quotationHeader, isNil),
    pInitialValues?.quotationHeader,
  );

  const { initialValues: quotationHeaderInitialValues } =
    useQuotationHeaderInitialValuesFactory(quotationHeaderInput);

  const initialValues: ResolvableMissionTypeForm_ResolvableMissionType =
    useMemo(
      () => ({
        __typename: 'ResolvableMissionType' as const,
        _id: generateId(),
        code: EMPTY_STRING,
        label: EMPTY_STRING,
        title: EMPTY_STRING,
        content: EMPTY_STRING,
        categoryParentId: EMPTY_ID,
        categoryLeaf: EMPTY_STRING,
        reportTemplateIds: EMPTY_ARRAY,
        allowedFileCategoryIds: EMPTY_ARRAY,
        quotationTemplateId: EMPTY_ID,
        enabledToolkitIds: EMPTY_ARRAY,
        statusId: defaultGenericStatus._id,
        serviceUsagesGroups: [
          {
            __typename: 'ResolvableServiceUsagesGroup' as const,
            _id: generateId(),
            groupingMode: GroupingModeEnum.Singleton,
            index: 0,
            label: null,
            serviceUsages: EMPTY_ARRAY,
          },
        ],
        ...omitBy(pInitialValues, isNil),
        quotationHeader: quotationHeaderInitialValues,
        codingConfig: {
          __typename: CodingStrategyEnum.CodingConfigManual as const,
          _id: generateId(),
          kind: CodingStrategyEnum.CodingConfigManual,
          strategyId: codingStrategiesCodeMap.CodingConfigManual._id,
          code: EMPTY_STRING,
          ...omitBy(pInitialValues?.codingConfig, isNil),
        },
      }),
      [
        codingStrategiesCodeMap.CodingConfigManual._id,
        defaultGenericStatus._id,
        quotationHeaderInitialValues,
        generateId,
        pInitialValues,
      ],
    );

  return { initialValues };
};
